'use client'
import { memo, useRef, useState, type FC } from 'react'
import styled from 'styled-components'
import { motion, useInView } from 'motion/react'
import { designGrid, mq, presets } from '@/styles'
import { SliderFlickity } from '@/components'
import { fadeFromBottomVariants } from '@/utils'
import type { TypeCmsContentSlider } from '@/schema'
import { getMediaPreview } from './ContentHeader'

type Props = {
  data: TypeCmsContentSlider['data']
}

type PropsItem = {
  data: TypeCmsContentSlider['data']['media'][0]
}


const Item:FC<PropsItem> = memo(({ data }) => {
  const [playVideo, setPlayVideo] = useState(true)

  return (
    <Article $playVideo={playVideo} className={`${data?.mediumType === 'image_and_video_files' || data?.mediumType === 'video_file' ? 'has-video' : ''}`}>
      {getMediaPreview(data, playVideo, setPlayVideo)}
    </Article>
  )
})

const headingId = 'slider'
const getItems = (media: Props['data']['media']) => media.map((item, idx) => (
  <Item key={`${idx}-${item.id}`} data={item} />
))


export const ContentSlider:FC<Props> = memo(({ data }) => {
  const { title, media } = data
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true })
  const Items = getItems(media)

  return (
    <Section aria-labelledby={headingId} ref={ref}>
      <motion.h2 id={headingId} variants={fadeFromBottomVariants} initial='initial' animate={isInView ? 'animate' : 'initial'}>
        {title}
      </motion.h2>
      <SliderFlickity className='content-slider' options={{ autoPlay: false, wrapAround: false, pageDots: false, cellAlign: 'left' }}>
        {Items}
        <div className='last-spacer' key={'last'}/>
      </SliderFlickity>
    </Section>
  )
})

const Section = styled.section`
  ${designGrid({})}
  margin-bottom: 50px;
  padding-top: 50px;
  width: 100vw;
  width: 100dvw;

  ${mq.greaterThan('tablet')} {
    margin-bottom: 70px;
    padding-top: 70px;
  }

  h2 {
    ${presets.font40_60()}
    grid-column: 1 / span 6;
    margin-bottom: 30px;
    text-align: left;

    ${mq.greaterThan('tablet')} {
      grid-column: 1 / span 12;
      margin-bottom: 50px;
      text-align: center;
    }
  }

  .slider-wrapper {
    grid-column: 1 / span 6;
    /* padding: 0 45px; */

    ${mq.greaterThan('tablet')} {
      grid-column: 1 / span 12;
      padding: 0;
    }
  }

  .last-spacer {
    margin-left: 300px;
  }
`


const Article = styled(motion.article)<{ $playVideo: boolean }>`
  &,
  &.flickity-cell {
    align-items: center;
    align-self: stretch;
    aspect-ratio: 47/50;
    display: flex;
    flex-shrink: 0;
    height: 300px;
    justify-content: center;
    margin-right: 10px;

    ${mq.greaterThan('tablet')} {
      height: 500px;
    }

    &.has-video {
      aspect-ratio: 16/9;
    }
  }

  img,
  .placeholder {
    aspect-ratio: 47/50;
    border-radius: 20px;
    height: 300px;
    object-fit: cover;
    overflow: hidden;
    width: 100%;

    ${mq.greaterThan('tablet')} {
      height: 500px;
    }
  }

  video {
    aspect-ratio: 16/9;
    background-color: var(--color-black);
    border-radius: 20px;
    height: 300px;
    object-fit: cover;
    overflow: hidden;
    width: 100%;

    ${mq.greaterThan('tablet')} {
      height: 500px;
    }
  }

  .placeholder {
    background-color: var(--color-black);
    opacity: .2;
  }
`
